import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, Modal } from '../components/Common';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://physio-scheduler-api.onrender.com/api';

interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: string;
  license_type: string;
  license_status: string;
  license_expires_at: string | null;
  license_requested_at: string | null;
  license_requested_type: string | null;
  created_at: string;
  updated_at: string;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

async function fetchUsers(): Promise<AdminUser[]> {
  const res = await fetch(`${API_URL}/admin/users`, { headers: getAuthHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch users');
  return data.users;
}

async function grantLicense(userId: number, type: string): Promise<AdminUser> {
  const res = await fetch(`${API_URL}/admin/users/${userId}/grant-license`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ type }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to grant licence');
  return data.user;
}

async function denyLicense(userId: number): Promise<AdminUser> {
  const res = await fetch(`${API_URL}/admin/users/${userId}/deny-license`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to deny request');
  return data.user;
}

async function updateLicense(
  userId: number,
  updates: { license_type?: string; license_status?: string; license_expires_at?: string | null }
): Promise<AdminUser> {
  const res = await fetch(`${API_URL}/admin/users/${userId}/license`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update licence');
  return data.user;
}

async function toggleAdmin(userId: number): Promise<AdminUser> {
  const res = await fetch(`${API_URL}/admin/users/${userId}/admin`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to toggle admin');
  return data.user;
}

function LicenceBadge({ type, status }: { type: string; status: string }) {
  if (type === 'none' || !type) {
    return <Badge variant="warning">No Licence</Badge>;
  }
  if (status === 'active') {
    return <Badge variant="success">{type === 'trial' ? 'Trial' : 'Permanent'}</Badge>;
  }
  if (status === 'expired') {
    return <Badge variant="danger">Expired</Badge>;
  }
  if (status === 'revoked') {
    return <Badge variant="danger">Revoked</Badge>;
  }
  return <Badge variant="warning">{status}</Badge>;
}

export function AdminUsers() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editType, setEditType] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editExpiry, setEditExpiry] = useState('');
  const [message, setMessage] = useState<{ success: boolean; text: string } | null>(null);

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: fetchUsers,
  });

  const grantMutation = useMutation({
    mutationFn: ({ userId, type }: { userId: number; type: string }) => grantLicense(userId, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setMessage({ success: true, text: 'Licence granted successfully' });
    },
    onError: (err: Error) => setMessage({ success: false, text: err.message }),
  });

  const denyMutation = useMutation({
    mutationFn: (userId: number) => denyLicense(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setMessage({ success: true, text: 'Licence request denied' });
    },
    onError: (err: Error) => setMessage({ success: false, text: err.message }),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      userId,
      updates,
    }: {
      userId: number;
      updates: { license_type?: string; license_status?: string; license_expires_at?: string | null };
    }) => updateLicense(userId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setEditUser(null);
      setMessage({ success: true, text: 'Licence updated successfully' });
    },
    onError: (err: Error) => setMessage({ success: false, text: err.message }),
  });

  const adminMutation = useMutation({
    mutationFn: (userId: number) => toggleAdmin(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setMessage({ success: true, text: 'Admin status toggled' });
    },
    onError: (err: Error) => setMessage({ success: false, text: err.message }),
  });

  const openEditModal = (u: AdminUser) => {
    setEditUser(u);
    setEditType(u.license_type || 'none');
    setEditStatus(u.license_status || 'inactive');
    setEditExpiry(u.license_expires_at ? u.license_expires_at.split('T')[0] : '');
  };

  const handleSaveEdit = () => {
    if (!editUser) return;
    updateMutation.mutate({
      userId: editUser.id,
      updates: {
        license_type: editType,
        license_status: editStatus,
        license_expires_at: editExpiry || null,
      },
    });
  };

  // Separate pending requests from other users
  const pendingRequests = users?.filter((u) => u.license_requested_at) || [];
  const allUsers = users || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-800 rounded-lg">
        Failed to load users: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">User Management</h1>

      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          <div className="flex items-center justify-between">
            <p>{message.text}</p>
            <button onClick={() => setMessage(null)} className="text-current opacity-50">
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Pending Licence Requests */}
      {pendingRequests.length > 0 && (
        <Card title={`Pending Licence Requests (${pendingRequests.length})`}>
          <div className="space-y-3">
            {pendingRequests.map((u) => (
              <div
                key={u.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg gap-3"
              >
                <div>
                  <p className="font-medium text-gray-900">{u.name}</p>
                  <p className="text-sm text-gray-500">{u.email}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Requesting: <strong>{u.license_requested_type}</strong> licence
                    {u.license_requested_at && (
                      <> &mdash; {new Date(u.license_requested_at).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      grantMutation.mutate({ userId: u.id, type: u.license_requested_type || 'trial' })
                    }
                    isLoading={grantMutation.isPending}
                  >
                    Grant {u.license_requested_type}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => denyMutation.mutate(u.id)}
                    isLoading={denyMutation.isPending}
                  >
                    Deny
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* All Users */}
      <Card title={`All Users (${allUsers.length})`}>
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="pb-3 font-medium">Name</th>
                <th className="pb-3 font-medium">Email</th>
                <th className="pb-3 font-medium">Role</th>
                <th className="pb-3 font-medium">Licence</th>
                <th className="pb-3 font-medium">Expires</th>
                <th className="pb-3 font-medium">Joined</th>
                <th className="pb-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allUsers.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 font-medium text-gray-900">{u.name}</td>
                  <td className="py-3 text-gray-500">{u.email}</td>
                  <td className="py-3">
                    <Badge variant={u.role === 'admin' ? 'info' : 'default'}>
                      {u.role}
                    </Badge>
                  </td>
                  <td className="py-3">
                    <LicenceBadge type={u.license_type} status={u.license_status} />
                  </td>
                  <td className="py-3 text-gray-500">
                    {u.license_expires_at
                      ? new Date(u.license_expires_at).toLocaleDateString()
                      : u.license_type === 'permanent' && u.license_status === 'active'
                      ? 'Never'
                      : '-'}
                  </td>
                  <td className="py-3 text-gray-500">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => openEditModal(u)}
                        className="px-2 py-1 text-xs text-primary-600 hover:bg-primary-50 rounded"
                      >
                        Edit
                      </button>
                      {u.id !== currentUser?.id && (
                        <button
                          onClick={() => adminMutation.mutate(u.id)}
                          className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                        >
                          {u.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {allUsers.map((u) => (
            <div key={u.id} className="p-3 border border-gray-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-gray-900">{u.name}</p>
                  <p className="text-sm text-gray-500">{u.email}</p>
                </div>
                <div className="flex gap-1">
                  <Badge variant={u.role === 'admin' ? 'info' : 'default'}>
                    {u.role}
                  </Badge>
                  <LicenceBadge type={u.license_type} status={u.license_status} />
                </div>
              </div>
              <div className="mt-2 flex gap-2 text-xs">
                {u.license_expires_at && (
                  <span className="text-gray-500">
                    Expires: {new Date(u.license_expires_at).toLocaleDateString()}
                  </span>
                )}
                <span className="text-gray-400">
                  Joined: {new Date(u.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="mt-2 flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => openEditModal(u)}>
                  Edit Licence
                </Button>
                {u.id !== currentUser?.id && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => adminMutation.mutate(u.id)}
                  >
                    {u.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Edit Licence Modal */}
      {editUser && (
        <Modal
          title={`Edit Licence - ${editUser.name}`}
          isOpen={true}
          onClose={() => setEditUser(null)}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Licence Type</label>
              <select
                value={editType}
                onChange={(e) => setEditType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="none">None</option>
                <option value="trial">Trial</option>
                <option value="permanent">Permanent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Licence Status</label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="inactive">Inactive</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
              <input
                type="date"
                value={editExpiry}
                onChange={(e) => setEditExpiry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank for no expiry</p>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="secondary" onClick={() => setEditUser(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEdit}
                isLoading={updateMutation.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
